// src/components/desktop/DesktopApp.jsx
import { useState, useCallback, useEffect } from "react";
import DesktopNavbar from "./navbar/DesktopNavbar";
import DesktopAppHome from "./desktopAppHome/DesktopAppHome";
import DesktopAppAdvertise from "./desktopAppAdvertise/DesktopAppAdvertise";
import DesktopAppMonetize from "./desktopAppMonetize/DesktopAppMonetize";
import DesktopAppRegisterPublisher from "./desktopAppMonetize/DesktopAppRegisterPublisher";
import DesktopAppRegisterAdvertiser from "./desktopAppMonetize/DesktopAppRegisterAdvertiser";
import DesktopAppFaq from "./desktopAppFaq/DesktopAppFaq";
import DesktopContact from "./desktopAppContact/DesktopContact";
// import DesktopAppFaq from "./desktopAppFaq/DesktopAppFaq";
// import DesktopAppAbout from "./desktopAppAbout/DesktopAppAbout";
import { useLang } from "../../i18n/LangContext";
import DesktopLogin from "./login/DesktopLogin";
import DesktopRegister from "./login/DesktopRegister";
import DesktopRegisterSuccess from "./login/DesktopRegisterSuccess";
import DesktopDashboard from "./dashboard/DesktopDashboard";
import DesktopVerifyEmail from "./login/DesktopVerifyEmail";
import DesktopResetPassword from "./login/DesktopResetPassword";
import { authApi, setAuthToken } from "../../api";
import PresentationPage from "../common/PresentationPage";

const ROUTES = {
  home: "home",
  advertise: "advertise",
  desktopAppRegisterPublisher: "registerPublisher",
  desktopAppRegisterAdvertiser: "registerAdvertiser",
  monetize: "monetize",
  faq: "faq",
  about: "about",
  dashboard: "dashboard",
  verifyEmail: "verifyEmail",
  resetPassword: "resetPassword",
  presentation: "presentation",
  contact: "contact",
};

const STORAGE_TOKEN_KEY = "auth_token";
const STORAGE_USER_KEY = "auth_user";

// mapping route -> URL path
const PATHS = {
  [ROUTES.home]: "/",
  [ROUTES.advertise]: "/advertise",
  [ROUTES.desktopAppRegisterPublisher]: "/registerPublisher",
  [ROUTES.desktopAppRegisterAdvertiser]: "/registerAdvertiser",
  [ROUTES.monetize]: "/monetize",
  [ROUTES.faq]: "/faq",
  [ROUTES.about]: "/about",
  [ROUTES.dashboard]: "/dashboard",
  [ROUTES.verifyEmail]: "/verify-email",
  [ROUTES.resetPassword]: "/reset-password",
  [ROUTES.presentation]: "/presentation",
  [ROUTES.contact]: "/contact",
};

function routeFromPath(pathname) {
  const path = pathname || "/";
  if (path === "/" || path === "") return ROUTES.home;

  // махаме trailing slash ако има
  const clean = path.replace(/\/+$/, "");
  switch (clean) {
    case "/advertise":
      return ROUTES.advertise;
    case "/registerPublisher":
      return ROUTES.desktopAppRegisterPublisher;
    case "/registerAdvertiser":
      return ROUTES.desktopAppRegisterAdvertiser;
    case "/faq":
      return ROUTES.faq;
    case "/dashboard":
      return ROUTES.dashboard;
    case "/verify-email":
      return ROUTES.verifyEmail;
    case "/reset-password":
      return ROUTES.resetPassword;
    case "/contact":
      return ROUTES.contact;
    case "/about":
      return ROUTES.about;
    case "/presentation":
      return ROUTES.presentation;
    default:
      return ROUTES.home;
  }
}

export default function DesktopApp() {
  const { t } = useLang();

  const [route, setRoute] = useState(() =>
    typeof window !== "undefined" ? routeFromPath(window.location.pathname) : ROUTES.home
  );
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showRegisterSuccess, setShowRegisterSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [pendingScrollId, setPendingScrollId] = useState(null);

  const sections = [
    { id: ROUTES.home, label: t.nav.home },
    { id: ROUTES.advertise, label: t.nav.advertise },
    { id: ROUTES.monetize, label: t.nav.monetize },
    { id: ROUTES.faq, label: t.nav.faq }
  ];

  // синхронизираме със back/forward
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onPopState = () => {
      const next = routeFromPath(window.location.pathname);
      setRoute(next);
      window.scrollTo({ top: 0, behavior: "instant" });
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);


  const scrollToId = useCallback((id) => {
    if (typeof window === "undefined") return;
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  useEffect(() => {
    if (!pendingScrollId) return;
    const id = pendingScrollId;
    const tId = setTimeout(() => {
      scrollToId(id);
      setPendingScrollId(null);
    }, 80);
    return () => clearTimeout(tId);
  }, [pendingScrollId, route, scrollToId]);

  const navigate = useCallback((nextRoute) => {
    if (nextRoute === route) return;

    setRoute(nextRoute);

    if (typeof window !== "undefined") {
      const nextPath = PATHS[nextRoute] || "/";
      window.history.pushState({}, "", nextPath);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [route]);

  const handleNavClick = useCallback(
    (nextRoute) => {
      navigate(nextRoute);
    },
    [navigate]
  );
  const openLogin = useCallback(() => setShowLogin(true), []);
  const closeLogin = useCallback(() => setShowLogin(false), []);
  const openRegister = useCallback(() => setShowRegister(true), []);
  const closeRegister = useCallback(() => setShowRegister(false), []);
  const openRegisterSuccess = useCallback((email = "") => {
    setRegisteredEmail(email);
    setShowRegisterSuccess(true);
  }, []);
  const closeRegisterSuccess = useCallback(() => setShowRegisterSuccess(false), []);
  const handleLogout = useCallback(() => {
    setAuthToken("");
    setCurrentUser(null);
    setShowLogin(false);
    setShowRegister(false);
    setShowRegisterSuccess(false);
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(STORAGE_TOKEN_KEY);
      window.localStorage.removeItem(STORAGE_USER_KEY);
    }
    navigate(ROUTES.home);
  }, [navigate]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const savedToken = window.localStorage.getItem(STORAGE_TOKEN_KEY);
    const savedUser = window.localStorage.getItem(STORAGE_USER_KEY);
    if (savedToken) {
      setAuthToken(savedToken);
      try {
        if (savedUser) {
          const parsed = JSON.parse(savedUser);
          setCurrentUser(parsed);
        }
      } catch (_) {
        // ignore parse errors
      }
      // attempt to refresh profile
      authApi
        .getProfile()
        .then((res) => {
          const user = res?.result?.user || res?.user;
          if (user) {
            setCurrentUser(user);
            window.localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(user));
          }
        })
        .catch(() => {});
    }
  }, []);

  const persistSession = useCallback((token, user) => {
    if (token) {
      setAuthToken(token);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_TOKEN_KEY, token);
      }
    }
    if (user && typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(user));
    }
  }, []);

  const goToPublisher = useCallback(() => navigate(ROUTES.desktopAppRegisterPublisher), [navigate]);
  const goToAdvertiser = useCallback(() => navigate(ROUTES.desktopAppRegisterAdvertiser), [navigate]);

  const handleContact = useCallback(() => {
    if (route === ROUTES.faq) {
      scrollToId("contact");
      return;
    }
    setPendingScrollId("contact");
    navigate(ROUTES.faq);
  }, [route, navigate, scrollToId]);

  const renderRoute = () => {
    switch (route) {
      case ROUTES.home:
        return <DesktopAppHome onGetStarted={goToPublisher} onContact={handleContact} />;
      case ROUTES.advertise:
        return <DesktopAppAdvertise onGetStarted={goToPublisher} onContact={handleContact} />;
      case ROUTES.monetize:
        return <DesktopAppMonetize onSelectAdvertiser={goToAdvertiser} onContact={handleContact} />;
      case ROUTES.desktopAppRegisterPublisher:
        return (
          <DesktopAppRegisterPublisher
            onSelectPublisher={goToPublisher}
            onSelectAdvertiser={goToAdvertiser}
            onLogin={openLogin}
            onRegister={openRegister}
          />
        );
      case ROUTES.desktopAppRegisterAdvertiser:
        return (
          <DesktopAppRegisterAdvertiser
            onSelectPublisher={goToPublisher}
            onSelectAdvertiser={goToAdvertiser}
            onLogin={openLogin}
            onRegister={openRegister}
          />
        );
      case ROUTES.dashboard:
        return <DesktopDashboard user={currentUser} onUpdate={setCurrentUser} />;
      case ROUTES.verifyEmail:
        return (
          <DesktopVerifyEmail
            onSuccess={({ token, user }) => {
              if (user) setCurrentUser(user);
              navigate(ROUTES.dashboard);
            }}
          />
        );
      case ROUTES.resetPassword:
        return (
          <DesktopResetPassword
            onSuccess={() => {
              navigate(ROUTES.home);
            }}
          />
        );
      case ROUTES.faq:
        return <DesktopAppFaq onGetStarted={goToPublisher} onContact={handleContact} />;
      case ROUTES.presentation:
        return <PresentationPage />;
      case ROUTES.contact:
        return <DesktopContact />;
      // case ROUTES.faq:
      //   return <DesktopAppFaq />;
      // case ROUTES.about:
      //   return <DesktopAppAbout />;
      default:
        return <DesktopAppHome />;
    }
  };

  return (
    <div className="app-root app-desktop">
      {/* TOP NAV – router режим */}
      {route !== ROUTES.presentation ? (
        <DesktopNavbar
          sections={sections}
          navHeight={88}
          activeId={route}
          onSelectSection={handleNavClick}
          hideLinks={route === ROUTES.dashboard}
          onLogin={openLogin}
          onRegister={openRegister}
          onDashboard={() => navigate(ROUTES.dashboard)}
          onLogout={handleLogout}
          isAuthenticated={!!currentUser}
        />
      ) : null}

      {/* PAGE CONTENT */}
      <main className="app-main">{renderRoute()}</main>

      <DesktopLogin
        open={showLogin}
        onClose={closeLogin}
        onSuccess={(result) => {
          setCurrentUser(result?.user || null);
          persistSession(result?.token, result?.user);
          closeLogin();
          navigate(ROUTES.dashboard);
        }}
        onRequireVerification={({ email }) => {
          closeLogin();
          setRegisteredEmail(email || "");
          setShowRegisterSuccess(true);
        }}
      />
      <DesktopRegister
        open={showRegister}
        onClose={closeRegister}
        onSuccess={(result) => {
          closeRegister();
          persistSession(result?.token, result?.user);
          openRegisterSuccess(result?.payload?.email || "");
        }}
      />
      <DesktopRegisterSuccess
        open={showRegisterSuccess}
        email={registeredEmail}
        onClose={closeRegisterSuccess}
      />
    </div>
  );
}